import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { chf } from "@/lib/format";
import { PageHeader, Loader, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessageSquare, Send, FileText, Check } from "lucide-react";

export default function Messages() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const [convs, setConvs] = useState(null);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadConvs = async () => { const { data } = await api.get(`/weddings/${wid}/conversations`); setConvs(data); if (!active && data[0]) setActive(data[0].id); };
  useEffect(() => { if (wid) loadConvs(); }, [wid]);

  const loadThread = async () => { if (!active) return; const { data } = await api.get(`/conversations/${active}/messages`); setThread(data); };
  useEffect(() => { loadThread(); }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [thread]);

  const send = async () => {
    if (!text.trim()) return;
    await api.post(`/conversations/${active}/messages`, { text });
    setText(""); loadThread(); loadConvs();
  };
  const acceptQuote = async (q) => {
    await api.post(`/weddings/${wid}/quotes/${q.id}/accept`);
    toast.success("Devis accepté — ajouté à votre budget et réservation créée");
    loadThread(); loadConvs();
  };

  if (!convs) return <Loader />;
  if (convs.length === 0) return (
    <div><PageHeader eyebrow="Prestataires" title="Messages" />
      <EmptyState icon={MessageSquare} title="Aucune conversation" description="Contactez un prestataire depuis la marketplace pour démarrer une discussion." /></div>
  );

  return (
    <div>
      <PageHeader eyebrow="Prestataires" title="Messages" />
      <div className="card-lux grid lg:grid-cols-[300px_1fr] overflow-hidden h-[calc(100vh-260px)]">
        <div className="border-r overflow-y-auto">
          {convs.map((c) => (
            <button key={c.id} onClick={() => setActive(c.id)} data-testid={`conv-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b hover:bg-accent/30 ${active === c.id ? "bg-accent/40" : ""}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-sm">{c.vendor_name}</div>
                {c.unread > 0 && <span className="w-2 h-2 rounded-full bg-champagne" />}</div>
            </button>
          ))}
        </div>
        <div className="flex flex-col">
          {thread ? (<>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {thread.messages.map((m) => {
                const mine = m.sender === "couple";
                const quote = m.type === "quote" ? thread.quotes.find((q) => q.id === m.quote_id) : null;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${mine ? "bg-stone-900 text-white" : "bg-accent"}`}>
                      {quote ? (
                        <div className="text-stone-900 bg-white rounded-xl p-3 -m-1 min-w-[220px]">
                          <div className="flex items-center gap-2 text-sm font-medium"><FileText className="w-4 h-4" /> Devis {quote.reference}</div>
                          <div className="font-mono text-xl mt-2">{chf(quote.total)}</div>
                          {quote.deposit > 0 && <div className="text-xs text-stone-500">Acompte {chf(quote.deposit)}</div>}
                          {quote.status === "accepted" ? (
                            <div className="text-sage text-sm flex items-center gap-1 mt-2"><Check className="w-4 h-4" /> Accepté</div>
                          ) : (
                            <Button size="sm" onClick={() => acceptQuote(quote)} data-testid={`accept-quote-${quote.id}`} className="w-full mt-2 rounded-full">Accepter</Button>
                          )}
                        </div>
                      ) : <div className="text-sm">{m.text}</div>}
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            <div className="border-t p-3 flex gap-2">
              <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Votre message…" className="h-11" data-testid="msg-input" />
              <Button onClick={send} className="rounded-full h-11 px-5" data-testid="msg-send"><Send className="w-4 h-4" /></Button>
            </div>
          </>) : <Loader />}
        </div>
      </div>
    </div>
  );
}
